// billing.action.js
import { billingConstants, orderConstants, authConstants } from "./constants";
import swal from "sweetalert";
import axios from "../helpers/axios";
import { getOffices } from "./officeAction";
import { closeAccessGate } from "./accessGate.actions";
import { patchActiveOfficeSalesSession } from "./officeSalesSession.actions";
import { emitOfficeSalesSessionAction } from "../socket/officeSalesSession.socket";
import {
    buildCanonicalSceneSnapshot,
    hasSceneSnapshotData,
    jsonByteLength,
} from "../utils/scenePersistence";


const ONE_TIME_SCENE_PRICE = 300;
const ONE_TIME_SCENE_EDIT_DAYS = 30;

const isMongoObjectId = (value) => /^[a-f\d]{24}$/i.test(String(value || '').trim());
const normalizeSalesSessionId = (value) => String(value || '').trim().toUpperCase();

const normalizeSceneIdentity = ({ sceneId = null, salesSessionId = null } = {}) => {
    const cleanSceneId = isMongoObjectId(sceneId) ? String(sceneId) : null;
    const cleanSalesSessionId = normalizeSalesSessionId(salesSessionId || (!cleanSceneId ? sceneId : ''));

    return {
        sceneId: cleanSceneId,
        salesSessionId: cleanSalesSessionId || null,
    };
};

const getSceneIdFromResponse = (data = {}) => {
    return data?.sceneSnapshot?._id || data?.sceneSnapshot?.id || data?.scene?._id || data?.scene?.id || null;
};

const getSceneAccessFromResponse = (data = {}) => {
    return data?.sceneAccess || data?.sceneSnapshot?.sceneAccess || null;
};




const patchSavedSceneToActiveSession = (dispatch, {
    responseData = {},
    salesSessionId = null,
} = {}) => {
    const sceneId = getSceneIdFromResponse(responseData);
    const sceneAccess = getSceneAccessFromResponse(responseData);
    const revision = Math.max(
        0,
        Number(responseData?.saveAck?.revision || responseData?.sceneSnapshot?.revision || responseData?.scene?.revision || 0)
    );
    const savedAt = (
        responseData?.saveAck?.savedAt ||
        responseData?.sceneSnapshot?.lastSavedAt ||
        responseData?.scene?.lastSavedAt ||
        responseData?.scene?.updatedAt ||
        null
    );

    if (!sceneId && !salesSessionId) return;

    const calc3dStatePatch = {
        sceneId: sceneId || null,
        sceneRevision: revision,
        savedAt,
        lastMode: 'saved-scene',
    };

    if (responseData?.saveAck?.integrity) {
        calc3dStatePatch.sceneSummary = responseData.saveAck.integrity;
    }

    const sessionPatch = {
        currentTab: 'calc3d',
        sceneSnapshotId: sceneId || null,
        calc3dState: calc3dStatePatch,
    };

    if (sceneAccess) {
        sessionPatch.sceneAccess = sceneAccess;
        sessionPatch.calc3dAccess = sceneAccess;
    }

    dispatch(patchActiveOfficeSalesSession(sessionPatch));
};

const publishCanonicalSceneSaved = ({
    officeId,
    salesSessionId,
    sceneId,
    revision = 0,
    savedAt = null,
    integrity = null,
} = {}) => {
    if (typeof window === 'undefined') return;

    const detail = {
        type: 'scene-updated',
        officeId: String(officeId || ''),
        sessionId: normalizeSalesSessionId(salesSessionId),
        sceneId: String(sceneId || ''),
        revision: Math.max(0, Number(revision || 0)),
        savedAt: savedAt || new Date().toISOString(),
        integrity: integrity || null,
        source: 'manual-save',
        ts: Date.now(),
    };

    window.dispatchEvent(new CustomEvent('officeSalesSession:canonicalSceneSaved', {
        detail,
    }));
    try {
        localStorage.setItem('ao_calc3d_scene_changed_v1', JSON.stringify(detail));
    } catch (_error) {
        // Сохранение уже завершено; межвкладочное уведомление не критично.
    }

    try {
        if (typeof BroadcastChannel !== 'undefined') {
            const channel = new BroadcastChannel('ao_calc3d_scene_channel_v1');
            channel.postMessage(detail);
            channel.close();
        }
    } catch (_error) {
        // BroadcastChannel может быть запрещён браузером.
    }

    // Остальным участникам живой сессии (другие сотрудники на своих компьютерах):
    // «сцена сохранена» — их вкладки подтянут сохранённую версию.
    try {
        if (detail.officeId && detail.sessionId) {
            emitOfficeSalesSessionAction({
                officeId: detail.officeId,
                sessionId: detail.sessionId,
                action: {
                    type: 'scene-saved',
                    label: 'Сцена сохранена',
                    source: 'manual-save',
                    meta: { sceneId: detail.sceneId, revision: detail.revision },
                },
            });
        }
    } catch (_error) {
        // Нет сокета — сохранение всё равно прошло.
    }
};

const isSceneWriteConflictError = (error = {}) => {
    const data = error?.response?.data || {};
    const message = String(data.error || data.message || error.message || '').toLowerCase();

    return !!(
        data.retryable === true ||
        data.code === 'SCENE_WRITE_CONFLICT_RETRY' ||
        message.includes('write conflict') ||
        message.includes('please retry your operation')
    );
};

const waitSceneRetry = (attempt) => new Promise((resolve) => {
    setTimeout(resolve, 250 + attempt * 350);
});

const postSceneWithRetry = async (url, payload, maxRetries = 2) => {
    let lastError = null;

    for (let attempt = 0; attempt <= maxRetries; attempt += 1) {
        try {
            return await axios.post(url, payload);
        } catch (error) {
            lastError = error;

            if (attempt < maxRetries && isSceneWriteConflictError(error)) {
                await waitSceneRetry(attempt);
                continue;
            }

            throw error;
        }
    }

    throw lastError;
};


export const initTopUp = (payload) => {
    return async (dispatch) => {
        try {
            dispatch({ type: billingConstants.INIT_TOPUP_REQUEST });

            const res = await axios.post("/billing/topup/init", payload);

            if (res.status === 200 && res.data.paymentUrl) {
                dispatch({
                    type: billingConstants.INIT_TOPUP_SUCCESS,
                    payload: res.data
                });

                if (res.data.mock) {
                    if (payload.targetModel === "User") {
                        dispatch({
                            type: authConstants.UPDATE_USER_BALANCE,
                            payload: res.data.newBalance
                        });
                    }

                    if (payload.targetModel === "Office") {
                        dispatch(getOffices());
                    }

                    swal("Успех", "Тестовое пополнение прошло успешно!", "success");
                    dispatch(getTransactionsHistory(payload.targetModel, payload.targetId));
                    return true;
                }

                window.location.href = res.data.paymentUrl;
                return true;
            }

            dispatch({
                type: billingConstants.INIT_TOPUP_FAILURE,
                payload: { error: "Не удалось получить ссылку на оплату" },
            });

            return false;
        } catch (error) {
            const errorMsg = error?.response?.data?.error || error.message;

            dispatch({
                type: billingConstants.INIT_TOPUP_FAILURE,
                payload: { error: errorMsg },
            });

            swal("Ошибка", errorMsg, "error");
            return false;
        }
    };
};

export const applyPromoCodeAction = (code, targetModel = "User", targetId) => {
    return async (dispatch) => {
        try {
            dispatch({ type: billingConstants.APPLY_PROMO_REQUEST });

            const res = await axios.post("/billing/promocode/apply", { code });

            if (res.status === 200) {
                if (res.data.newBalance !== undefined && res.data.newBalance !== null) {
                    dispatch({
                        type: authConstants.UPDATE_USER_BALANCE,
                        payload: res.data.newBalance
                    });
                }

                if (res.data.subscription) {
                    dispatch({
                        type: authConstants.UPDATE_USER_SUBSCRIPTION,
                        payload: res.data.subscription
                    });
                }

                dispatch({
                    type: billingConstants.APPLY_PROMO_SUCCESS,
                    payload: res.data
                });

                swal("Успех", res.data.message, "success");

                if (targetModel && targetId) {
                    dispatch(getTransactionsHistory(targetModel, targetId));
                }

                return true;
            }

            dispatch({
                type: billingConstants.APPLY_PROMO_FAILURE,
                payload: { error: "Не удалось активировать промокод" },
            });

            return false;
        } catch (error) {
            const errorMsg = error?.response?.data?.error || error.message;

            swal("Ошибка", errorMsg, "error");

            dispatch({
                type: billingConstants.APPLY_PROMO_FAILURE,
                payload: { error: errorMsg },
            });

            return false;
        }
    };
};

export const payForScene = (orderId) => {
    return async (dispatch) => {
        try {
            dispatch({ type: billingConstants.PAY_SCENE_REQUEST });

            const res = await axios.post("/billing/scene/pay", { orderId });

            if (res.status === 200) {
                swal({
                    title: "Успех",
                    text: res.data.message,
                    icon: "success",
                });

                dispatch({
                    type: billingConstants.PAY_SCENE_SUCCESS,
                    payload: res.data
                });

                dispatch({
                    type: orderConstants.GET_USER_ORDER_DETAILS_SUCCESS,
                    payload: { order: res.data.order }
                });

                if (res.data.newBalance !== undefined && res.data.order?.scope !== "office") {
                    dispatch({
                        type: authConstants.UPDATE_USER_BALANCE,
                        payload: res.data.newBalance
                    });
                }

                if (res.data.order?.scope === "office") {
                    dispatch(getOffices());
                }

                return true;
            }

            dispatch({
                type: billingConstants.PAY_SCENE_FAILURE,
                payload: { error: "Ошибка оплаты сцены" },
            });

            return false;
        } catch (error) {
            const errorMsg = error?.response?.data?.error || error.message;

            swal({
                title: "Ошибка",
                text: errorMsg,
                icon: "warning",
                buttons: {
                    cancel: "Закрыть",
                    topup: {
                        text: "Пополнить баланс",
                        value: "topup",
                    },
                },
            }).then((value) => {
                if (value === "topup") {
                    window.location.href = "/personalInformation";
                }
            });

            dispatch({
                type: billingConstants.PAY_SCENE_FAILURE,
                payload: { error: errorMsg },
            });

            return false;
        }
    };
};


export const payForSceneAccess = ({
    officeId,
    sceneId = null,
    salesSessionId = null,
    amount = ONE_TIME_SCENE_PRICE,
    accessMode = "permanent",
    editDays = ONE_TIME_SCENE_EDIT_DAYS,
    title = "",
    snapshot = null,
    viewCheckpoint = null,
    expectedRevision = null,
    explicitFieldClears = [],
} = {}) => {
    return async (dispatch, getState) => {
        try {
            dispatch({ type: billingConstants.PAY_SCENE_ACCESS_REQUEST });

            const identity = normalizeSceneIdentity({ sceneId, salesSessionId });
            const state = getState?.() || {};
            const built = snapshot
                ? { snapshot, productsMeta: null }
                : buildCanonicalSceneSnapshot(state, {
                    officeId,
                    sceneId: identity.sceneId,
                    salesSessionId: identity.salesSessionId,
                    title,
                    viewCheckpoint,
                    editDays,
                });
            const finalSnapshot = built.snapshot;

            if (built.productsMeta && typeof console !== 'undefined') {
                console.log('[SCENE-SAVE][compact]', {
                    productsMetaTotal: built.productsMeta.total,
                    productsMetaKept: built.productsMeta.kept,
                    productsMetaBeforeBytes: built.productsMeta.beforeBytes,
                    productsMetaAfterBytes: built.productsMeta.afterBytes,
                    productsMetaSavedPercent: built.productsMeta.beforeBytes > 0
                        ? Number((((built.productsMeta.beforeBytes - built.productsMeta.afterBytes) / built.productsMeta.beforeBytes) * 100).toFixed(1))
                        : 0,
                    snapshotBytes: jsonByteLength(finalSnapshot),
                });
            }

            const res = await postSceneWithRetry("/billing/scene-access/pay", {
                officeId,
                sceneId: identity.sceneId,
                salesSessionId: identity.salesSessionId,
                sessionId: identity.salesSessionId,
                title,
                amount,
                price: amount,
                accessMode,
                viewAccess: "permanent",
                editDays,
                snapshot: finalSnapshot,
                expectedRevision: Number.isFinite(Number(expectedRevision))
                    ? Number(expectedRevision)
                    : null,
                explicitFieldClears: Array.isArray(explicitFieldClears)
                    ? explicitFieldClears
                    : [],
            });

            if (res.status === 200) {
                dispatch({
                    type: billingConstants.PAY_SCENE_ACCESS_SUCCESS,
                    payload: res.data,
                });

                if (res.data.userBalance !== undefined) {
                    dispatch({
                        type: authConstants.UPDATE_USER_BALANCE,
                        payload: res.data.userBalance,
                    });
                }

                dispatch(getOffices());
                patchSavedSceneToActiveSession(dispatch, {
                    responseData: res.data,
                    salesSessionId: identity.salesSessionId,
                });
                dispatch(closeAccessGate());

                const savedSceneId = getSceneIdFromResponse(res.data);
                const revision = Number(res.data?.saveAck?.revision || res.data?.sceneSnapshot?.revision || res.data?.scene?.revision || 0);
                publishCanonicalSceneSaved({
                    officeId,
                    salesSessionId: identity.salesSessionId,
                    sceneId: savedSceneId,
                    revision,
                    savedAt: res.data?.saveAck?.savedAt || res.data?.sceneSnapshot?.lastSavedAt || res.data?.scene?.lastSavedAt || null,
                    integrity: res.data?.saveAck?.integrity || null,
                });

                swal(
                    "Сцена оплачена",
                    res.data.message || "Просмотр этой сцены доступен навсегда. Редактирование доступно 30 дней.",
                    "success"
                );

                return {
                    ok: true,
                    data: res.data,
                    sceneSnapshot: res.data.sceneSnapshot || null,
                    sceneAccess: res.data.sceneAccess || null,
                };
            }

            dispatch({
                type: billingConstants.PAY_SCENE_ACCESS_FAILURE,
                payload: { error: "Ошибка оплаты разовой сцены" },
            });

            return {
                ok: false,
                error: "Ошибка оплаты разовой сцены",
            };
        } catch (error) {
            const data = error?.response?.data || {};
            const status = Number(error?.response?.status || 0);
            const errorMsg = data.error || data.message || error.message;
            const backendNotReady = status === 404 || String(errorMsg || "").toLowerCase().includes("not found");

            dispatch({
                type: billingConstants.PAY_SCENE_ACCESS_FAILURE,
                payload: {
                    error: backendNotReady
                        ? "Фронт разовой сцены подготовлен. Нужно подключить backend endpoint /billing/scene-access/pay."
                        : errorMsg,
                },
            });

            if (status === 409 && data.code === 'SCENE_PARTIAL_REGRESSION_SUSPECTED') {
                return {
                    ok: false,
                    suspiciousRegression: true,
                    code: data.code,
                    error: errorMsg,
                    suspiciousFields: Array.isArray(data.suspiciousFields) ? data.suspiciousFields : [],
                    regression: Array.isArray(data.regression) ? data.regression : [],
                    currentRevision: Number(data.currentRevision || 0),
                };
            }

            if (status === 409 && data.code === 'SCENE_REVISION_CONFLICT') {
                return {
                    ok: false,
                    revisionConflict: true,
                    code: data.code,
                    error: errorMsg,
                    currentRevision: Number(data.currentRevision || 0),
                };
            }

            swal(
                backendNotReady ? "Разовая сцена подготовлена" : "Ошибка",
                backendNotReady
                    ? "Кнопка и snapshot сцены уже собраны на фронте. Следующий шаг — backend: SceneSnapshot, SceneAccess и endpoint оплаты."
                    : errorMsg,
                backendNotReady ? "info" : "error"
            );

            return {
                ok: false,
                error: errorMsg,
                code: data.code || 'SCENE_PAYMENT_ERROR',
            };
        }
    };
};


export const saveSceneSnapshot = ({
    officeId,
    sceneId = null,
    salesSessionId = null,
    title = '',
    snapshot = null,
    viewCheckpoint = null,
    silent = false,
    expectedRevision = null,
    explicitFieldClears = [],
} = {}) => {
    return async (dispatch, getState) => {
        const identity = normalizeSceneIdentity({ sceneId, salesSessionId });

        try {
            const state = getState?.() || {};
            const built = snapshot
                ? { snapshot, productsMeta: null }
                : buildCanonicalSceneSnapshot(state, {
                    officeId,
                    sceneId: identity.sceneId,
                    salesSessionId: identity.salesSessionId,
                    title,
                    viewCheckpoint,
                    editDays: ONE_TIME_SCENE_EDIT_DAYS,
                });
            const finalSnapshot = built.snapshot;

            if (built.productsMeta && typeof console !== 'undefined') {
                console.log('[SCENE-SAVE][compact]', {
                    productsMetaTotal: built.productsMeta.total,
                    productsMetaKept: built.productsMeta.kept,
                    productsMetaBeforeBytes: built.productsMeta.beforeBytes,
                    productsMetaAfterBytes: built.productsMeta.afterBytes,
                    productsMetaSavedPercent: built.productsMeta.beforeBytes > 0
                        ? Number((((built.productsMeta.beforeBytes - built.productsMeta.afterBytes) / built.productsMeta.beforeBytes) * 100).toFixed(1))
                        : 0,
                    snapshotBytes: jsonByteLength(finalSnapshot),
                });
            }

            const res = await postSceneWithRetry('/scene-snapshot', {
                officeId,
                sceneId: identity.sceneId,
                salesSessionId: identity.salesSessionId,
                sessionId: identity.salesSessionId,
                title,
                snapshot: finalSnapshot,
                expectedRevision: Number.isFinite(Number(expectedRevision))
                    ? Number(expectedRevision)
                    : null,
                explicitFieldClears: Array.isArray(explicitFieldClears)
                    ? explicitFieldClears
                    : [],
            });

            if (res.status === 200) {
                dispatch(getOffices());
                patchSavedSceneToActiveSession(dispatch, {
                    responseData: res.data,
                    salesSessionId: identity.salesSessionId,
                });

                const savedSceneId = getSceneIdFromResponse(res.data);
                const revision = Number(res.data?.saveAck?.revision || res.data?.sceneSnapshot?.revision || res.data?.scene?.revision || 0);
                publishCanonicalSceneSaved({
                    officeId,
                    salesSessionId: identity.salesSessionId,
                    sceneId: savedSceneId,
                    revision,
                    savedAt: res.data?.saveAck?.savedAt || res.data?.sceneSnapshot?.lastSavedAt || res.data?.scene?.lastSavedAt || null,
                    integrity: res.data?.saveAck?.integrity || null,
                });

                if (!silent) {
                    swal(
                        'Сцена сохранена',
                        res.data.message || 'Состояние 3D-сцены сохранено.',
                        'success'
                    );
                }

                return {
                    ok: true,
                    data: res.data,
                    sceneSnapshot: res.data.sceneSnapshot || null,
                    sceneAccess: res.data.sceneAccess || null,
                    saveAck: res.data.saveAck || null,
                };
            }

            return {
                ok: false,
                error: 'Не удалось сохранить сцену',
            };
        } catch (error) {
            const data = error?.response?.data || {};
            const status = Number(error?.response?.status || 0);
            const errorMsg = data.error || data.message || error.message;

            if (status === 402 || data.code === 'SCENE_PAYMENT_REQUIRED') {
                const state = getState?.() || {};
                const built = snapshot
                    ? { snapshot }
                    : buildCanonicalSceneSnapshot(state, {
                        officeId,
                        sceneId: identity.sceneId,
                        salesSessionId: data.salesSessionId || identity.salesSessionId || null,
                        title,
                        viewCheckpoint,
                        editDays: data.editDays || ONE_TIME_SCENE_EDIT_DAYS,
                    });

                return {
                    ok: false,
                    needsPayment: true,
                    code: data.code || 'SCENE_PAYMENT_REQUIRED',
                    error: errorMsg,
                    price: data.price || ONE_TIME_SCENE_PRICE,
                    editDays: data.editDays || ONE_TIME_SCENE_EDIT_DAYS,
                    sceneId: isMongoObjectId(data.sceneId || identity.sceneId) ? (data.sceneId || identity.sceneId) : null,
                    salesSessionId: data.salesSessionId || identity.salesSessionId || null,
                    snapshot: built.snapshot,
                    currentRevision: Number(data.currentRevision || expectedRevision || 0),
                };
            }

            if (status === 409 && data.code === 'SCENE_PARTIAL_REGRESSION_SUSPECTED') {
                return {
                    ok: false,
                    suspiciousRegression: true,
                    code: data.code,
                    error: errorMsg,
                    suspiciousFields: Array.isArray(data.suspiciousFields) ? data.suspiciousFields : [],
                    regression: Array.isArray(data.regression) ? data.regression : [],
                    currentRevision: Number(data.currentRevision || 0),
                };
            }

            if (status === 409 && data.code === 'SCENE_REVISION_CONFLICT') {
                return {
                    ok: false,
                    revisionConflict: true,
                    code: data.code,
                    error: errorMsg,
                    currentRevision: Number(data.currentRevision || 0),
                };
            }

            if (!silent) {
                swal('Ошибка', errorMsg, 'error');
            }

            return {
                ok: false,
                error: errorMsg,
                code: data.code || 'SCENE_SAVE_ERROR',
            };
        }
    };
};

export const getTransactionsHistory = (targetModel, targetId) => {
    return async (dispatch) => {
        try {
            if (!targetModel || !targetId) return null;

            dispatch({ type: billingConstants.GET_TRANSACTIONS_REQUEST });

            const res = await axios.get(`/billing/transactions?targetModel=${targetModel}&targetId=${targetId}`);

            if (res.status === 200) {
                dispatch({
                    type: billingConstants.GET_TRANSACTIONS_SUCCESS,
                    payload: res.data.transactions || []
                });

                return res.data.transactions || [];
            }

            dispatch({
                type: billingConstants.GET_TRANSACTIONS_FAILURE,
                payload: { error: "Ошибка загрузки истории операций" },
            });

            return null;
        } catch (error) {
            dispatch({
                type: billingConstants.GET_TRANSACTIONS_FAILURE,
                payload: { error: error?.response?.data?.error || error.message },
            });

            return null;
        }
    };
};

export const buySubscription = ({ targetModel = "Office", targetId, plan }) => {
    return async (dispatch) => {
        try {
            dispatch({ type: billingConstants.BUY_SUBSCRIPTION_REQUEST });

            const res = await axios.post("/billing/subscription/buy", {
                targetModel,
                targetId,
                plan
            });

            if (res.status === 200) {
                dispatch({
                    type: billingConstants.BUY_SUBSCRIPTION_SUCCESS,
                    payload: res.data
                });

                if (targetModel === "User") {
                    if (res.data.newBalance !== undefined) {
                        dispatch({
                            type: authConstants.UPDATE_USER_BALANCE,
                            payload: res.data.newBalance
                        });
                    }

                    if (res.data.subscription) {
                        dispatch({
                            type: authConstants.UPDATE_USER_SUBSCRIPTION,
                            payload: res.data.subscription
                        });
                    }
                }

                if (targetModel === "Office") {
                    dispatch(getOffices());
                }

                swal("Успех", res.data.message || "Подписка активирована", "success");
                dispatch(getTransactionsHistory(targetModel, targetId));

                return true;
            }

            dispatch({
                type: billingConstants.BUY_SUBSCRIPTION_FAILURE,
                payload: { error: "Ошибка покупки подписки" }
            });

            return false;
        } catch (error) {
            const errorMsg = error?.response?.data?.error || error.message;

            dispatch({
                type: billingConstants.BUY_SUBSCRIPTION_FAILURE,
                payload: { error: errorMsg }
            });

            swal("Ошибка", errorMsg, "error");
            return false;
        }
    };
};

export const cancelSubscription = ({ targetModel = "Office", targetId }) => {
    return async (dispatch) => {
        try {
            dispatch({ type: billingConstants.CANCEL_SUBSCRIPTION_REQUEST });

            const confirmed = await swal({
                title: "Отключить автопродление?",
                text: "Подписка останется активной до конца оплаченного периода. Возврат средств за уже оплаченный период не производится.",
                icon: "warning",
                buttons: ["Отмена", "Отключить"],
                dangerMode: true,
            });

            if (!confirmed) {
                dispatch({
                    type: billingConstants.CANCEL_SUBSCRIPTION_FAILURE,
                    payload: { error: "" }
                });

                return false;
            }

            const res = await axios.post("/billing/subscription/cancel", {
                targetModel,
                targetId
            });

            if (res.status === 200) {
                dispatch({
                    type: billingConstants.CANCEL_SUBSCRIPTION_SUCCESS,
                    payload: res.data
                });

                if (targetModel === "User" && res.data.subscription) {
                    dispatch({
                        type: authConstants.UPDATE_USER_SUBSCRIPTION,
                        payload: res.data.subscription
                    });
                }

                if (targetModel === "Office") {
                    dispatch(getOffices());
                }

                swal("Готово", res.data.message, "success");
                dispatch(getTransactionsHistory(targetModel, targetId));

                return true;
            }

            dispatch({
                type: billingConstants.CANCEL_SUBSCRIPTION_FAILURE,
                payload: { error: "Ошибка отключения автопродления" }
            });

            return false;
        } catch (error) {
            const errorMsg = error?.response?.data?.error || error.message;

            dispatch({
                type: billingConstants.CANCEL_SUBSCRIPTION_FAILURE,
                payload: { error: errorMsg }
            });

            if (errorMsg) swal("Ошибка", errorMsg, "error");
            return false;
        }
    };
};

export const enableSubscriptionAutoRenew = ({ targetModel = "Office", targetId }) => {
    return async (dispatch) => {
        try {
            dispatch({ type: billingConstants.ENABLE_SUBSCRIPTION_AUTORENEW_REQUEST });

            const res = await axios.post("/billing/subscription/enable-auto-renew", {
                targetModel,
                targetId
            });

            if (res.status === 200) {
                dispatch({
                    type: billingConstants.ENABLE_SUBSCRIPTION_AUTORENEW_SUCCESS,
                    payload: res.data
                });

                if (targetModel === "User" && res.data.subscription) {
                    dispatch({
                        type: authConstants.UPDATE_USER_SUBSCRIPTION,
                        payload: res.data.subscription
                    });
                }

                if (targetModel === "Office") {
                    dispatch(getOffices());
                }

                swal("Готово", res.data.message, "success");
                dispatch(getTransactionsHistory(targetModel, targetId));

                return true;
            }

            dispatch({
                type: billingConstants.ENABLE_SUBSCRIPTION_AUTORENEW_FAILURE,
                payload: { error: "Ошибка включения автопродления" }
            });

            return false;
        } catch (error) {
            const errorMsg = error?.response?.data?.error || error.message;

            dispatch({
                type: billingConstants.ENABLE_SUBSCRIPTION_AUTORENEW_FAILURE,
                payload: { error: errorMsg }
            });

            swal("Ошибка", errorMsg, "error");
            return false;
        }
    };
};

export const transferToOffice = ({ officeId, amount, description }) => {
    return async (dispatch) => {
        try {
            dispatch({ type: billingConstants.TRANSFER_TO_OFFICE_REQUEST });

            const res = await axios.post("/billing/transfer-to-office", {
                officeId,
                amount,
                description
            });

            if (res.status === 200) {
                dispatch({
                    type: billingConstants.TRANSFER_TO_OFFICE_SUCCESS,
                    payload: res.data
                });

                if (res.data.userBalance !== undefined) {
                    dispatch({
                        type: authConstants.UPDATE_USER_BALANCE,
                        payload: res.data.userBalance
                    });
                }

                dispatch(getOffices());

                swal("Успех", res.data.message || "Средства переведены на офис", "success");

                return true;
            }

            dispatch({
                type: billingConstants.TRANSFER_TO_OFFICE_FAILURE,
                payload: { error: "Ошибка перевода средств" }
            });

            return false;
        } catch (error) {
            const errorMsg = error?.response?.data?.error || error.message;

            dispatch({
                type: billingConstants.TRANSFER_TO_OFFICE_FAILURE,
                payload: { error: errorMsg }
            });

            swal("Ошибка", errorMsg, "error");
            return false;
        }
    };
};

export const setCloseAlertBilling = () => ({
    type: billingConstants.CLOSE_BILLING_ALERT
});


export const getOfficeBillingSummary = (officeId) => {
    return async () => {
        try {
            const res = await axios.get(`/billing/office/${officeId}/summary`);
            return res.data?.summary || null;
        } catch (_error) {
            return null;
        }
    };
};

export const buyOfficeScenePackage = ({ officeId, packageKey }) => {
    return async (dispatch) => {
        try {
            const res = await axios.post(`/billing/office/${officeId}/scene-package/buy`, { packageKey });
            if (res.status === 200) {
                dispatch(getOffices());
                dispatch(getTransactionsHistory('Office', officeId));
                swal('Успех', res.data.message || 'Пакет сцен куплен', 'success');
                return { ok: true, data: res.data };
            }
            return { ok: false };
        } catch (error) {
            const errorMsg = error?.response?.data?.error || error.message;
            swal('Ошибка', errorMsg, 'error');
            return { ok: false, error: errorMsg };
        }
    };
};

export const previewOfficeStoragePackage = ({ officeId, packageKey }) => {
    return async () => {
        try {
            const res = await axios.post(`/billing/office/${officeId}/storage-package/preview`, { packageKey });
            return { ok: true, data: res.data?.preview || null };
        } catch (error) {
            return {
                ok: false,
                error: error?.response?.data?.error || error.message,
                code: error?.response?.data?.code || 'STORAGE_PACKAGE_PREVIEW_ERROR',
            };
        }
    };
};

export const buyOfficeStoragePackage = ({ officeId, packageKey }) => {
    return async (dispatch) => {
        try {
            const res = await axios.post(`/billing/office/${officeId}/storage-package/buy`, { packageKey });

            if (res.status === 200) {
                dispatch(getOffices());
                dispatch(getTransactionsHistory('Office', officeId));
                return { ok: true, data: res.data };
            }

            return { ok: false, error: 'Не удалось увеличить хранилище' };
        } catch (error) {
            return {
                ok: false,
                error: error?.response?.data?.error || error.message,
                code: error?.response?.data?.code || 'STORAGE_PACKAGE_BUY_ERROR',
            };
        }
    };
};
